import type { CollapsePropSize, CollapsePropHorizontalSpace, CollapseIconPropDirection } from 'uikit';
import { Collapse } from 'uikit';

import cn from 'classnames';
import type { BaseSyntheticEvent, ReactNode } from 'react';
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';

import { useSaveCollapseState } from './useSaveCollapseState';

import './CustomCollapse.css';

type CustomCollapseOpenStateProps<T> = T extends boolean
    ? { isOpen?: boolean; saveCollapseStateKey?: never }
    : {
          isOpen?: never;
          saveCollapseStateKey?: string;
      };

interface CustomCollapseProps {
    id?: string;
    label: string;
    size?: CollapsePropSize;
    horizontalSpace?: CollapsePropHorizontalSpace;
    rightSide?: ReactNode;
    directionIcon?: CollapseIconPropDirection;
    closeDirectionIcon?: CollapseIconPropDirection;
    level?: 'first' | 'second' | 'third' | 'fourth' | 'fifth';
    hoverEffect?: boolean;
    children?: ReactNode;
    onClick?: (() => void) | ((event: BaseSyntheticEvent) => void);
    className?: string;
    emptyCollapse?: boolean;
    suppressAnimation?: boolean;
    isActiveLink?: boolean;
    isLabelGrey?: boolean;
}

export const CustomCollapse = <T = any,>({
    id,
    label,
    size,
    horizontalSpace,
    rightSide,
    directionIcon,
    closeDirectionIcon,
    isOpen,
    hoverEffect,
    level,
    className,
    children,
    emptyCollapse,
    onClick,
    suppressAnimation = true,
    isActiveLink,
    saveCollapseStateKey,
    isLabelGrey,
}: CustomCollapseProps & CustomCollapseOpenStateProps<T>) => {
    const refCollapse = useRef<HTMLDivElement>(null);
    const { handleSaveTreeState, handleCheckIsOpen } = useSaveCollapseState();

    const collapseKey = useMemo(() => `collapseState_${saveCollapseStateKey}_isOpened`, [saveCollapseStateKey]);

    const [open, setOpen] = useState(() => isOpen || (!!saveCollapseStateKey && handleCheckIsOpen(collapseKey)));
    const [heightBtn, setHeightBtn] = useState<number>();

    const toggleOpenCollapse = useCallback(() => {
        setOpen((prevState) => !prevState);
    }, []);

    useEffect(() => {
        if (saveCollapseStateKey) {
            handleSaveTreeState({
                key: collapseKey,
                newState: open,
            });
        }
    }, [collapseKey, handleSaveTreeState, open, saveCollapseStateKey]);

    useEffect(() => {
        setHeightBtn(refCollapse?.current?.firstElementChild?.clientHeight);

        return () => {
            setHeightBtn(undefined);
        };
    }, []);

    const currentLabel = useMemo(() => {
        if (isLabelGrey) {
            return (
                <div className="Text Text_lineHeight_m Text_size_s Text_view_secondary Collapse-LabelText">{label}</div>
            );
        }

        return label;
    }, [isLabelGrey, label]);

    return (
        <div
            className={cn('CustomCollapse__wrapper', {
                isActiveLink,
                emptyCollapse,
                CustomCollapse__wrapper_level_3: level === 'third',
                CustomCollapse__wrapper_level_4: level === 'fourth',
                CustomCollapse__wrapper_level_5: level === 'fifth',
            })}
        >
            <button
                className={cn('CustomCollapse__button', {
                    emptyCollapse,
                    CustomCollapse__button_level_1: level === 'first',
                    CustomCollapse__button_level_2: level === 'second',
                    CustomCollapse__button_level_3: level === 'third',
                    CustomCollapse__button_level_4: level === 'fourth',
                    CustomCollapse__button_level_5: level === 'fifth',
                })}
                style={{ height: heightBtn }}
                type="button"
                onClick={toggleOpenCollapse}
            />
            <Collapse
                ref={refCollapse}
                id={id}
                className={cn(className, {
                    suppressAnimation,
                })}
                label={currentLabel}
                isOpen={open}
                size={size}
                horizontalSpace={horizontalSpace}
                directionIcon={directionIcon}
                closeDirectionIcon={closeDirectionIcon}
                rightSide={rightSide}
                hoverEffect={hoverEffect}
                onClick={onClick}
                iconPosition="left"
                style={{ width: '100%', position: 'relative' }}
            >
                {children}
            </Collapse>
        </div>
    );
};

CustomCollapse.displayName = 'CustomCollapse';
